import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedPresaveWidget, SerializedWidget } from 'fym-common/src/db/serializersTypes';
import { blockMessages } from './blockMessages';
import { describeBlock } from './describeBlock';
import { EditorSaveTracker } from '../saves/editorSaveTracker';
import { useDeleteWithUndo } from './useDeleteWithUndo';

interface UseRequestDeleteProps {
  accountId: number;
  smartLinkId: number;
  artistName: string | null;
  /** The row closes when its block goes. */
  onDeleted: (widgetUUID: string) => void;
  showNotice: (message: string) => void;
  saves: EditorSaveTracker;
}

/** True for Catalog links with a pre-save that still takes sign-ups. */
function hasOpenPresave(widget: SerializedWidget): widget is SerializedPresaveWidget {
  return (
    widget.type === SmartLinkWidgetType.presave &&
    widget.data.hasCampaign !== false &&
    widget.data.campaignUuid !== '' &&
    !widget.data.released &&
    !widget.data.closed
  );
}

/**
 * Delete of a block (design d10): no question, the row hides at once and the Undo toast shows. The
 * one exception: Catalog links with an open pre-save asks first when it has fans
 * (`SMLConfirmCatalogDelete`).
 */
export function useRequestDelete({
  accountId,
  smartLinkId,
  artistName,
  onDeleted,
  showNotice,
  saves,
}: UseRequestDeleteProps) {
  const intl = useIntl();
  const [confirmFor, setConfirmFor] = useState<SerializedPresaveWidget | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});

  const onFailed = useCallback(
    (widgetUUID: string) =>
      showNotice(intl.formatMessage(blockMessages.deleteFailed, { name: names[widgetUUID] ?? '' })),
    [intl, names, showNotice]
  );
  const deletes = useDeleteWithUndo({ accountId, smartLinkId, onFailed, saves });

  const removeNow = useCallback(
    (widget: SerializedWidget) => {
      const { name } = describeBlock(intl, widget, { artistName });
      setNames((current) => ({ ...current, [widget.uuid]: name }));
      deletes.remove({
        widgetUUID: widget.uuid,
        message:
          widget.type === SmartLinkWidgetType.presaveSubscribe
            ? intl.formatMessage(blockMessages.deletedSubscribe)
            : intl.formatMessage(blockMessages.deleted, { name }),
      });
      onDeleted(widget.uuid);
    },
    [artistName, deletes, intl, onDeleted]
  );

  const requestDelete = useCallback(
    (widget: SerializedWidget) => {
      if (hasOpenPresave(widget)) {
        setConfirmFor(widget);
        return;
      }
      removeNow(widget);
    },
    [removeNow]
  );

  const confirm = useCallback(() => {
    if (confirmFor !== null) {
      removeNow(confirmFor);
    }
    setConfirmFor(null);
  }, [confirmFor, removeNow]);
  const cancel = useCallback(() => setConfirmFor(null), []);

  return { deletes, requestDelete, confirmFor, confirm, cancel };
}
